import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, effect, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental';
import { BucketService } from '../../services/bucket.service';
import { Bucket } from '../../types';
import { TransactionService } from '../../services/transaction.service';
import { GroupService } from '../../services/group.service';
import { TransactionDataSource, TransactionMutationActions } from '../../transactions/transaction-data-source.class';
import { MonthService } from '../../services/month.service';
import { firstValueFrom, from, Subject } from 'rxjs';
import { filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { AccountService } from '../../services/account.service';
import { InfiniteTransactionListComponent } from '../../transactions/infinite-transactions-list/infinite-transaction-list.component';
import { UiButtonDirective } from '../../ui/button/button.directive';
import { RouterLink } from '@angular/router';
import { UserService } from '../../services/user.service';
import { AnalyticsService } from '../../services/analytics.service';
import { transactionAnalyticsHooks } from '../../analytics/transaction-analytics';

@Component({
  selector: 'app-bucket-transaction-list',
  templateUrl: './bucket-transaction-list.component.html',
  styleUrl: './bucket-transaction-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InfiniteTransactionListComponent, UiButtonDirective, RouterLink],
})
export class BucketTransactionListComponent implements OnDestroy {
  bucketService = inject(BucketService);
  private transactionService = inject(TransactionService);
  groupService = inject(GroupService);
  private monthService = inject(MonthService);
  accountService = inject(AccountService);
  private userService = inject(UserService);
  private analytics = inject(AnalyticsService);

  private readonly queryClient = injectQueryClient();
  bucket = input<Bucket | null>(null);
  loading = input(false);
  showEmptyActions = input(true);
  readonly accountsQuery = injectQuery(() => this.accountService.accountPageQueryOptions());
  private readonly updateTransactionMutation = injectMutation(() =>
    this.transactionService.updateTransactionMutationOptions(),
  );
  private readonly updateMultipleTransactionsMutation = injectMutation(() =>
    this.transactionService.updateMultipleTransactionsMutationOptions(),
  );
  private readonly deleteTransactionMutation = injectMutation(() =>
    this.transactionService.deleteTransactionMutationOptions(),
  );
  private readonly deleteTransferMutation = injectMutation(() =>
    this.transactionService.deleteTransferMutationOptions(),
  );
  private readonly undeleteTransactionMutation = injectMutation(() =>
    this.transactionService.undeleteTransactionMutationOptions(),
  );
  private readonly transactionMutations: TransactionMutationActions = {
    updateTransaction: data => this.updateTransactionMutation.mutateAsync(data),
    updateMultipleTransactions: (data, previousTransactions) =>
      this.updateMultipleTransactionsMutation.mutateAsync({ data, previousTransactions }),
    deleteTransaction: (transactionId, monthYear) =>
      this.deleteTransactionMutation.mutateAsync({ transactionId, monthYear }),
    deleteTransfer: (transferId, monthYear) => this.deleteTransferMutation.mutateAsync({ transferId, monthYear }),
    undeleteTransaction: (transactionId, monthYear) =>
      this.undeleteTransactionMutation.mutateAsync({ transactionId, monthYear }),
  };
  private readonly destroyRef = inject(DestroyRef);

  hideTransactionList = false;
  // Assigned by setupDataSource() as soon as the bucket input arrives.
  public transactionDataSource!: TransactionDataSource;
  private readonly transactionDataSourceChange$ = new Subject<void>();

  constructor() {
    effect(() => {
      const bucket = this.bucket();
      this.hideTransactionList = !bucket || bucket.transactions.totalRecords === 0;
      if (bucket) {
        this.setupDataSource(
          new TransactionDataSource(bucket.transactions, this.transactionService, this.bucketService, {
            withoutDateHeaders: false,
            loadFirstPageOnly: false,
            loadMoreTransactions: () => this.loadMoreBucketTransactions(bucket),
            refreshTransactions: () => this.refreshBucketTransactions(bucket),
            refreshSession: () => this.queryClient.fetchQuery(this.userService.refreshSessionQueryOptions()),
            ...transactionAnalyticsHooks(this.analytics),
            mutations: this.transactionMutations,
          }),
        );
      }
    });
  }

  private setupDataSource(data: TransactionDataSource) {
    this.transactionDataSourceChange$.next();
    this.transactionDataSource = data;
    data.transactionsLoading$
      .pipe(
        takeUntil(this.transactionDataSourceChange$),
        takeUntilDestroyed(this.destroyRef),
        filter(loading => !loading),
        switchMap(() =>
          this.monthService.currentMonthYear$.pipe(
            take(1),
            switchMap(month => from(this.queryClient.fetchQuery(this.groupService.groupPageQueryOptions(month)))),
          ),
        ),
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.transactionDataSourceChange$.next();
    this.transactionDataSourceChange$.complete();
  }

  private loadMoreBucketTransactions(bucket: Bucket) {
    return firstValueFrom(
      this.transactionDataSource.transactionPage$.pipe(
        take(1),
        switchMap(page =>
          this.queryClient
            .fetchQuery(
              this.transactionService.transactionRequestQueryOptions({
                ...page.params,
                bucketIds: [bucket.id],
                monthYear: bucket.monthYear,
                pageNumber: page.pageNumber + 1,
                pageSize: page.pageSize,
                suggestedBucketIds: true,
              }),
            )
            .then(nextPage => ({ ...nextPage, data: [...page.data, ...nextPage.data] })),
        ),
      ),
    );
  }

  private refreshBucketTransactions(bucket: Bucket) {
    return firstValueFrom(
      this.transactionDataSource.transactionPage$.pipe(
        take(1),
        switchMap(page =>
          this.queryClient.fetchQuery(
            this.transactionService.transactionRequestQueryOptions({
              ...page.params,
              bucketIds: [bucket.id],
              monthYear: bucket.monthYear,
              pageNumber: 1,
              pageSize: page.pageSize,
              suggestedBucketIds: true,
            }),
          ),
        ),
      ),
    );
  }
}
